/**
 * Appointment Scheduler Component
 * Handles appointment type selection, resource/facility selection, date picking, and time range selection
 */

import React, { useState, useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { 
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { TimeRangePicker } from '@/components/ui/time-range-picker';
import { Calendar, Clock, Video, Building, Search, AlertTriangle, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, parse } from 'date-fns';
import { 
  useAppointmentScheduling, 
  AppointmentType,
  SelectedTimeRange 
} from '@/hooks/appointments/useAppointmentScheduling';
import { Resource, Facility } from '@/services/api/appointments/appointmentSchedulingService';

interface AppointmentSchedulerProps {
  clinicId?: number;
  patientTimezone?: string;
  disabled?: boolean;
  onAppointmentChange?: (appointmentData: AppointmentData | null) => void;
}

export interface AppointmentData {
  appointmentType: AppointmentType;
  resourceId?: number;
  facilityId?: number;
  date: string;
  startTime: string;
  endTime: string;
  availabilityDayTimeId: number;
  timezone?: string;
}

const AppointmentScheduler: React.FC<AppointmentSchedulerProps> = ({
  clinicId,
  patientTimezone: patientTimezoneProp,
  disabled = false,
  onAppointmentChange
}) => {
  const {
    appointmentType,
    selectedResource,
    selectedFacility,
    selectedDate,
    selectedTimeRange,
    resources,
    facilities,
    availableDates,
    timeSlots,
    bookedAppointments,
    patientTimezone,
    loadingResources,
    loadingFacilities,
    loadingDates,
    loadingTimeSlots,
    error,
    conflictWarning,
    currentMonth,
    handleAppointmentTypeChange,
    handleResourceSelect,
    handleFacilitySelect,
    handleDateSelect,
    handleTimeRangeSelect,
    handleMonthChange,
    getCombinedTimeRange
  } = useAppointmentScheduling(clinicId, patientTimezoneProp);

  const [resourceSearchOpen, setResourceSearchOpen] = useState(false);
  const [facilitySearchOpen, setFacilitySearchOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [resourceSearchQuery, setResourceSearchQuery] = useState('');
  const [facilitySearchQuery, setFacilitySearchQuery] = useState('');

  // Time picker state
  const [startTimeInput, setStartTimeInput] = useState('');
  const [endTimeInput, setEndTimeInput] = useState('');

  // Filter resources based on search
  const filteredResources = useMemo(() => {
    if (!resourceSearchQuery.trim()) return resources;
    const query = resourceSearchQuery.toLowerCase();
    return resources.filter(r => 
      r.email?.toLowerCase().includes(query) ||
      r.user_name?.toLowerCase().includes(query) ||
      r.user_type?.toLowerCase().includes(query)
    );
  }, [resources, resourceSearchQuery]);

  // Filter facilities based on search
  const filteredFacilities = useMemo(() => {
    if (!facilitySearchQuery.trim()) return facilities;
    const query = facilitySearchQuery.toLowerCase();
    return facilities.filter(f => 
      f.facility_name?.toLowerCase().includes(query)
    );
  }, [facilities, facilitySearchQuery]);

  // Get combined time range
  const combinedTimeRange = useMemo(() => getCombinedTimeRange(), [getCombinedTimeRange]);

  // Handle time changes from TimeRangePicker
  const handleStartTimeChange = (time: string) => {
    setStartTimeInput(time);
  };

  const handleEndTimeChange = (time: string) => {
    setEndTimeInput(time);
  };

  // Format time for display (24h to 12h)
  const formatTimeDisplay = (time24: string) => {
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
    } catch {
      return time24;
    }
  };

  // Handle time range confirmation
  const handleTimeRangeConfirm = () => {
    if (!startTimeInput || !endTimeInput || !combinedTimeRange) return;

    const timeRange: SelectedTimeRange = {
      startTime: startTimeInput,
      endTime: endTimeInput,
      availabilityDayTimeId: combinedTimeRange.availabilityDayTimeId
    };

    handleTimeRangeSelect(timeRange);

    // Notify parent of appointment data
    if (onAppointmentChange && selectedDate) {
      const appointmentData: AppointmentData = {
        appointmentType,
        resourceId: selectedResource?.id,
        facilityId: selectedFacility?.clinic_facility_id,
        date: format(selectedDate, 'yyyy-MM-dd'),
        startTime: startTimeInput,
        endTime: endTimeInput,
        availabilityDayTimeId: combinedTimeRange.availabilityDayTimeId,
        timezone: patientTimezone
      };
      onAppointmentChange(appointmentData);
    }
  };

  // Clear time selection
  const handleClearTimeSelection = () => {
    setStartTimeInput('');
    setEndTimeInput('');
    handleTimeRangeSelect(null);
    onAppointmentChange?.(null);
  };

  // Auto-confirm when both times are set
  React.useEffect(() => {
    if (startTimeInput && endTimeInput && combinedTimeRange && selectedDate) {
      const timeRange: SelectedTimeRange = {
        startTime: startTimeInput,
        endTime: endTimeInput,
        availabilityDayTimeId: combinedTimeRange.availabilityDayTimeId
      };

      handleTimeRangeSelect(timeRange);

      // Notify parent of appointment data
      if (onAppointmentChange) {
        const appointmentData: AppointmentData = {
          appointmentType,
          resourceId: selectedResource?.id,
          facilityId: selectedFacility?.clinic_facility_id,
          date: format(selectedDate, 'yyyy-MM-dd'),
          startTime: startTimeInput,
          endTime: endTimeInput,
          availabilityDayTimeId: combinedTimeRange.availabilityDayTimeId,
          timezone: patientTimezone
        };
        onAppointmentChange(appointmentData);
      }
    }
  }, [startTimeInput, endTimeInput]);

  // Clear time inputs when date changes
  React.useEffect(() => {
    setStartTimeInput('');
    setEndTimeInput('');
  }, [selectedDate]);

  // Check if a date is available
  const isDateAvailable = (date: Date) => {
    const formatted = format(date, 'yyyy-MM-dd');
    return availableDates.includes(formatted);
  };

  // Calendar modifiers
  const modifiers = useMemo(() => ({
    available: (date: Date) => isDateAvailable(date),
    disabled: (date: Date) => !isDateAvailable(date) || date < new Date(new Date().setHours(0, 0, 0, 0))
  }), [availableDates]);

  const modifiersStyles = {
    available: {
      backgroundColor: 'hsl(var(--primary) / 0.1)',
      color: 'hsl(var(--primary))',
      fontWeight: 600
    }
  };

  return (
    <div className="space-y-4">
      {/* Appointment Type Selection */}
      <div>
        <Label className="text-sm font-semibold text-gray-700">
          Appointment Type
        </Label>
        <Select
          value={appointmentType || ''}
          onValueChange={(value) => handleAppointmentTypeChange(value as AppointmentType)}
          disabled={disabled}
        >
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select appointment type" />
          </SelectTrigger>
          <SelectContent className="bg-background z-50">
            <SelectItem value="virtual">
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4" />
                <span>Virtual</span>
              </div>
            </SelectItem>
            <SelectItem value="in-person">
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                <span>In Person</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Resource Selection (Virtual) */}
      {appointmentType === 'virtual' && (
        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Select Resource
          </Label>
          <Popover open={resourceSearchOpen} onOpenChange={setResourceSearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={resourceSearchOpen}
                className="w-full justify-between mt-1"
                disabled={disabled || loadingResources}
              >
                {selectedResource ? (
                  <span className="truncate">
                    {selectedResource.user_name} ({selectedResource.email})
                  </span>
                ) : (
                  <span className="text-muted-foreground">
                    {loadingResources ? 'Loading resources...' : 'Search and select a resource'}
                  </span>
                )}
                <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0 bg-background z-50" align="start">
              <Command shouldFilter={false}>
                <CommandInput 
                  placeholder="Search by email, name, or type..." 
                  value={resourceSearchQuery}
                  onValueChange={setResourceSearchQuery}
                />
                <CommandList>
                  <CommandEmpty>No resources found.</CommandEmpty>
                  <CommandGroup>
                    {filteredResources.map((resource) => (
                      <CommandItem
                        key={resource.id}
                        value={resource.id.toString()}
                        onSelect={() => {
                          handleResourceSelect(resource);
                          setResourceSearchOpen(false);
                          setResourceSearchQuery('');
                        }}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "h-4 w-4",
                            selectedResource?.id === resource.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <div className="flex flex-col">
                          <span className="font-medium">{resource.user_name}</span>
                          <span className="text-xs text-muted-foreground">{resource.email}</span>
                        </div>
                        <Badge variant="outline" className="ml-auto text-xs">
                          {resource.user_type}
                        </Badge>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Facility Selection (In-Person) */}
      {appointmentType === 'in-person' && (
        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Select Facility
          </Label>
          <Popover open={facilitySearchOpen} onOpenChange={setFacilitySearchOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={facilitySearchOpen}
                className="w-full justify-between mt-1"
                disabled={disabled || loadingFacilities}
              >
                {selectedFacility ? (
                  <span className="truncate">{selectedFacility.facility_name}</span>
                ) : (
                  <span className="text-muted-foreground">
                    {loadingFacilities ? 'Loading facilities...' : 'Search and select a facility'}
                  </span>
                )}
                <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0 bg-background z-50" align="start">
              <Command shouldFilter={false}>
                <CommandInput 
                  placeholder="Search by facility name..." 
                  value={facilitySearchQuery}
                  onValueChange={setFacilitySearchQuery}
                />
                <CommandList>
                  <CommandEmpty>No facilities found.</CommandEmpty>
                  <CommandGroup>
                    {filteredFacilities.map((facility) => (
                      <CommandItem
                        key={facility.clinic_facility_id}
                        value={facility.clinic_facility_id.toString()}
                        onSelect={() => {
                          handleFacilitySelect(facility);
                          setFacilitySearchOpen(false);
                          setFacilitySearchQuery('');
                        }}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "h-4 w-4",
                            selectedFacility?.clinic_facility_id === facility.clinic_facility_id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <Building className="h-4 w-4 text-muted-foreground" />
                        <span>{facility.facility_name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Date Selection */}
      {((appointmentType === 'virtual' && selectedResource) || 
        (appointmentType === 'in-person' && selectedFacility)) && (
        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Appointment Date
          </Label>
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal mt-1",
                  !selectedDate && "text-muted-foreground"
                )}
                disabled={disabled || loadingDates}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {selectedDate ? (
                  format(selectedDate, 'MMMM d, yyyy')
                ) : (
                  <span>{loadingDates ? 'Loading dates...' : 'Select a date'}</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-background z-50 pointer-events-auto" align="start">
              <div className="p-3">
                {/* Custom header with month navigation */}
                <div className="flex items-center justify-between mb-4">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      const prevMonth = new Date(currentMonth);
                      prevMonth.setMonth(prevMonth.getMonth() - 1);
                      handleMonthChange(prevMonth);
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-medium">
                    {format(currentMonth, 'MMMM yyyy')}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      const nextMonth = new Date(currentMonth);
                      nextMonth.setMonth(nextMonth.getMonth() + 1);
                      handleMonthChange(nextMonth);
                    }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                
                {loadingDates ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <DayPicker
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      handleDateSelect(date);
                      setDatePickerOpen(false);
                    }}
                    month={currentMonth}
                    onMonthChange={handleMonthChange}
                    disabled={modifiers.disabled}
                    modifiers={{ available: modifiers.available }}
                    modifiersStyles={modifiersStyles}
                    showOutsideDays={false}
                    className="pointer-events-auto"
                    classNames={{
                      months: "flex flex-col",
                      month: "space-y-4",
                      caption: "hidden",
                      caption_label: "text-sm font-medium",
                      nav: "hidden",
                      table: "w-full border-collapse",
                      head_row: "flex",
                      head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem] flex-1 text-center",
                      row: "flex w-full mt-2",
                      cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 flex-1 h-9",
                      day: cn(
                        "h-9 w-9 p-0 font-normal mx-auto flex items-center justify-center rounded-md",
                        "hover:bg-accent hover:text-accent-foreground cursor-pointer",
                        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      ),
                      day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
                      day_today: "bg-accent text-accent-foreground",
                      day_outside: "text-muted-foreground opacity-50",
                      day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed hover:bg-transparent",
                      day_hidden: "invisible"
                    }}
                  />
                )}
                
                {patientTimezone && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Timezone: {patientTimezone}
                  </p>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Time Selection */}
      {selectedDate && (
        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Appointment Time
          </Label>
          
          {loadingTimeSlots ? (
            <div className="flex items-center gap-2 mt-2 text-muted-foreground">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              <span className="text-sm">Loading time slots...</span>
            </div>
          ) : timeSlots.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-2">No time slots available for this date.</p>
          ) : (
            <div className="mt-2 space-y-3">
              {/* Available time slots list */}
              {timeSlots.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2 text-blue-800">
                    <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <span className="text-sm font-medium block">Available Time Slots{patientTimezone ? ` (${patientTimezone})` : ''}:</span>
                      <ul className="text-sm space-y-0.5 list-disc list-inside ml-1">
                        {timeSlots.map((slot, index) => (
                          <li key={index}>
                            {formatTimeDisplay(slot.startTime)} - {formatTimeDisplay(slot.endTime)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Time range picker */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Select Time Range</Label>
                <TimeRangePicker
                  startTime={startTimeInput}
                  endTime={endTimeInput}
                  onStartTimeChange={handleStartTimeChange}
                  onEndTimeChange={handleEndTimeChange}
                  availableSlots={timeSlots.map(slot => ({
                    startTime: slot.startTime,
                    endTime: slot.endTime
                  }))}
                  disabled={disabled}
                />
              </div>

              {/* Conflict warning */}
              {conflictWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm">{conflictWarning}</span>
                  </div>
                </div>
              )}

              {/* Booked appointments list */}
              {bookedAppointments.length > 0 && (
                <div className="mt-3">
                  <Label className="text-xs text-muted-foreground">Existing Appointments</Label>
                  <div className="mt-1 space-y-1">
                    {bookedAppointments.map((apt) => (
                      <div 
                        key={apt.appointmentId} 
                        className="text-xs bg-gray-100 rounded px-2 py-1 flex justify-between"
                      >
                        <span>{apt.displayTime}</span>
                        <span className="text-muted-foreground">{apt.appointmentReason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
    </div>
  );
};

export default AppointmentScheduler;
